import React, { useEffect, useState } from 'react'

import { Popover } from '@mui/material'

import type {
  TChatRatingReason,
  TSimChatRatingReason
} from '@homepal/api-contract'

import { CHAT_RATING_REASONS } from 'types/GlobalChat'

import { useTranslation } from 'language/translator'

/* Atoms */
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

type AnyReason = TChatRatingReason | TSimChatRatingReason

interface ChatFeedbackPopoverProps<Reason extends AnyReason> {
  anchorEl: HTMLElement | null
  onClose: () => void
  onSubmit: (reason: Reason, comment: string | null) => void

  // The categories offered as chips, each with a `feedback_reason_<reason>`
  // label in the chat namespace. The metric chat's reasons by default.
  reasons?: readonly Reason[]
}

const ChatFeedbackPopover = <Reason extends AnyReason = TChatRatingReason>(
  props: ChatFeedbackPopoverProps<Reason>
) => {
  const { anchorEl, onClose, onSubmit } = props
  const reasons = (props.reasons ?? CHAT_RATING_REASONS) as readonly Reason[]

  const { t } = useTranslation()

  const label = (option: AnyReason) => t('chat', `feedback_reason_${option}`)

  const [reason, setReason] = useState<Reason | null>(null)
  const [comment, setComment] = useState('')

  // Start from a blank form every time the popover is opened again.
  useEffect(() => {
    if (anchorEl) {
      setReason(null)
      setComment('')
    }
  }, [anchorEl])

  const submit = () => {
    if (!reason) return

    onSubmit(reason, comment.trim() || null)
  }

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      onClose={onClose}
      open={Boolean(anchorEl)}
      slotProps={{ paper: { sx: { maxWidth: 320, p: 2 } } }}
    >
      <Text gutterBottom={true} variant="subtitle2">
        {t('chat', 'feedback_title')}
      </Text>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
        {reasons.map((option) => (
          <Chip
            color={reason === option ? 'primary' : 'default'}
            key={option}
            label={label(option)}
            onClick={() => setReason(option)}
            size="small"
            variant={reason === option ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      <TextField
        fullWidth={true}
        maxRows={4}
        multiline={true}
        onChange={(event) => setComment(event.target.value)}
        placeholder={t('chat', 'feedback_comment_placeholder')}
        size="small"
        value={comment}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Button disabled={!reason} onClick={submit} size="small">
          {t('chat', 'feedback_submit')}
        </Button>
      </Box>
    </Popover>
  )
}

export default ChatFeedbackPopover
